<template>
  <v-col :class="columnClasses">
    <v-card
      :class="'pa-2 fill-height d-flex flex-column border '+cardClass"
      :elevation="elevation"
      rounded="md"
      @click="$router.push(bookSlugUrl)"
    >
      <v-card-text class="d-flex flex-column flex-grow-1 pa-0">
        <div class="cover-image mb-2">
          <book-cover-image
            contain
            height="100%"
            max-height="100%"
            :src="coverSrc"
            :alt="$t('bookInfo.bookCoverAlt', { title: resolvedTitle })"
            @error="imageError = true"
          >
            <ekitabu-chip
              v-if="formatDisplayText"
              color="lightAccent"
              small
              class="text-caption format-chip"
            >
              {{ formatDisplayText }}
            </ekitabu-chip>
            <v-tooltip
              v-if="isOwned"
              top
            >
              <template #activator="{ on }">
                <ekitabu-icon
                  class="owned-icon pa-1 ma-0"
                  color="background"
                  v-on="on"
                >
                  mdi-bookshelf
                </ekitabu-icon>
              </template>
              <span>{{ $t("bookshelf.inBookshelf") }}</span>
            </v-tooltip>
          </book-cover-image>
        </div>
        <div class="d-flex flex-column flex-grow-1 justify-space-between">
          <h5 class="text-caption font-weight-bold black--text">
            {{ decodeHtml(resolvedTitle) }}
          </h5>
          <div>
            <PriceCartPrompt
              :id="id"
              :price="resolvedPrice"
              :currency-id="resolvedCurrency?._id"
              :is-owned="isOwned"
              :is-owned-loading="ownershipLoading"
              :no-iri-id="noIriId"
              :show-cart-button="showCartButton && showDefaultCartActions"
              :refetch-bookshelf="refetchOwnedBookshelf"
              :book="bookObject"
              :vat-included="resolvedVatIncluded"
            />
            <div
              v-if="showDefaultCartActions && isBuyNowAction"
              :class="stackBuyNowButtons ? 'd-flex flex-column mt-2' : 'd-flex mt-2 buy-now-actions'"
            >
              <add-to-cart-button
                :id="id"
                :book="bookObject"
                :simplified-id="noIriId"
                :query-loading="cartButtonQueryLoading"
                buy-now
                color1="formatPrint"
                color2="formatPrint"
                :class="stackBuyNowButtons ? '' : 'flex-grow-1 flex-shrink-1 mr-1'"
                :style="stackBuyNowButtons ? '' : 'min-width: 0;'"
                :add-to-cart-text="$t('shop.buyNow')"
                icon="mdi-flash"
                :icon-classes="stackBuyNowButtons ? 'ml-2 has-glow' : 'ml-1'"
                block
              />
              <add-to-cart-button
                :id="id"
                :book="bookObject"
                :simplified-id="noIriId"
                :query-loading="cartButtonQueryLoading"
                :class="stackBuyNowButtons ? 'mt-1' : 'flex-grow-1 flex-shrink-1 ml-1'"
                :style="stackBuyNowButtons ? '' : 'min-width: 0;'"
                block
                :add-to-cart-text="$t('shop.addToCart')"
                :show-icon="stackBuyNowButtons"
              />
            </div>
            <add-to-cart-button
              v-else-if="showDefaultCartActions && showAddToCartButton"
              :id="id"
              :book="bookObject"
              button-classes="mt-2"
              :simplified-id="noIriId"
              :query-loading="cartButtonQueryLoading"
              block
              :add-to-cart-text="$t('shop.addToCart')"
            />
            <open-book-button
              v-else-if="isOwned && showCartButton && !isPrintFormat"
              :read-url="readUrl"
              :format="resolvedFormat"
              :book-id="id"
              block
              :audio-text="$t('bookInfo.listenShort')"
            />
          </div>
        </div>
      </v-card-text>
      <slot />
    </v-card>
  </v-col>
</template>

<script>
import { decodeHtml } from "@/helpers/generalFunctions";
import BookCoverImage from "@/components/BookCoverImage";
import AddToCartButton from "@/views/checkout/AddToCartButton.vue";
import PrimaryButton from "@/components/buttons/PrimaryButton.vue";
import { removeIriId } from "@/iris";
import { buildBookUrl } from "@/helpers/slugHelper";
import OpenBookButton from "@/components/OpenBookButton";
import PriceCartPrompt from "@/components/PriceCartPrompt.vue";
import BookshelfMixin from "@/mixins/BookshelfMixin";
import { getFormat, isPrintFormat as isPrintFormatHelper } from "@/utils/BookFormatHelper";
import fallbackCover from "@/assets/img/eKitabu-bug.png";

export default {
  components: {
    BookCoverImage,
    AddToCartButton,
    OpenBookButton,
    PriceCartPrompt,
  },
  mixins: [BookshelfMixin],
  props: {
    book: {
      type: Object,
      required: false,
      default: null,
    },
    // Individual field props are optional overrides. When `book` is passed,
    // the component reads fields from it. These props exist for callers
    // whose data shape differs from the book object (e.g. UserBookList).
    price: {
      type: [String, Number],
      required: false,
      default: null,
    },
    id: {
      type: String,
      required: true,
    },
    thumbnailUrl: {
      type: String,
      required: false,
      default: null,
    },
    title: {
      type: String,
      required: false,
      default: null,
    },
    currency: {
      type: Object,
      required: false,
      default: null,
    },
    columnClasses: {
      type: String,
      default: "col-6 col-sm-3 col-lg-2 pa-1 pa-md-2",
    },
    showBuyNow: {
      type: Boolean,
      required: false,
      default: false,
    },
    showCartButton: {
      type: Boolean,
      default: true,
    },
    format: {
      type: [String, Object],
      required: false,
      default: null,
    },
    elevation: {
      type: String,
      default: "16",
    },
    cardClass: {
      type: String,
      default: "border--default",
    },
    vatIncluded: {
      type: [Boolean],
      default: null,
    },
    stackBuyNowButtons: {
      type: Boolean,
      default: true,
    },
    useExternalOwnership: {
      type: Boolean,
      default: false,
    },
    externalOwnedBook: {
      type: Object,
      default: null,
    },
    externalBookshelfLoading: {
      type: Boolean,
      default: false,
    },
    externalRefetchBookshelf: {
      type: Function,
      default: () => {},
    },
    showDefaultCartActions: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      imageError: false,
    };
  },
  computed: {
    // Resolved fields: prefer explicit prop override, fall back to book object
    resolvedTitle() {
      return this.title ?? this.book?.title ?? '';
    },
    resolvedThumbnailUrl() {
      return this.thumbnailUrl ?? this.book?.thumbnailUrl ?? this.book?.imageUrl ?? '';
    },
    resolvedPrice() {
      return this.price ?? this.book?.price ?? null;
    },
    resolvedCurrency() {
      return this.currency ?? this.book?.currency ?? null;
    },
    resolvedFormat() {
      return this.format ?? this.book?.format ?? null;
    },
    resolvedVatIncluded() {
      return this.vatIncluded ?? this.book?.vatIncluded ?? false;
    },
    bookSlugUrl() {
      return buildBookUrl({ id: this.id, title: this.resolvedTitle, slug: this.book?.slug });
    },
    coverSrc() {
      return this.imageError ? fallbackCover : this.resolvedThumbnailUrl;
    },
    noIriId() {
      return removeIriId(this.id);
    },
    bookObject() {
      if (this.book) {
        return { ...this.book, format: this.resolvedFormat, vatIncluded: this.resolvedVatIncluded };
      }
      return {
        price: this.resolvedPrice,
        title: this.resolvedTitle,
        thumbnailUrl: this.resolvedThumbnailUrl,
        currency: this.resolvedCurrency,
        format: this.resolvedFormat,
        vatIncluded: this.resolvedVatIncluded,
        _id: this.noIriId,
      };
    },
    disableBookshelfOwnershipQuery() {
      return this.useExternalOwnership;
    },
    ownedBook() {
      return this.useExternalOwnership ? this.externalOwnedBook : this.checkInBookshelf(this.id);
    },
    isOwned() {
      return !!this.ownedBook;
    },
    readUrl() {
      return this.ownedBook?.readUrl ?? null;
    },
    ownershipLoading() {
      return this.useExternalOwnership ? this.externalBookshelfLoading : this.bookshelfLoading;
    },
    cartButtonQueryLoading() {
      return this.isPrintFormat ? false : this.ownershipLoading;
    },
    formatDisplayText() {
      if (!this.resolvedFormat) return null;
      
      // Handle case where format is an object (LocalBook)
      if (typeof this.resolvedFormat === 'object' && this.resolvedFormat.mimeType) {
        return getFormat(this.resolvedFormat.mimeType);
      }
      
      // Handle case where format is a string (regular Book)
      if (typeof this.resolvedFormat === 'string') {
        return getFormat(this.resolvedFormat);
      }
      
      return null;
    },
    isPrintFormat() {
      return isPrintFormatHelper(this.resolvedFormat);
    },
    showAddToCartButton() {
      const hasPaidPrice = this.price > 0;
      const printBuyNow = hasPaidPrice && this.isPrintFormat;
      const unownedBuyNow = !this.isOwned && hasPaidPrice && this.showBuyNow;
      const ownedPrintReorder = this.isOwned && this.showCartButton && this.isPrintFormat;
      return printBuyNow || unownedBuyNow || ownedPrintReorder;
    },
    isBuyNowAction() {
      const hasPaidPrice = this.price > 0;
      return hasPaidPrice && (this.isPrintFormat || this.showBuyNow);
    },
  },
  methods: {
    decodeHtml(text) {
      return decodeHtml(text);
    },
    refetchOwnedBookshelf(productIds) {
      return this.useExternalOwnership
        ? this.externalRefetchBookshelf(productIds)
        : this.refetchBookshelf(productIds);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/styles.sass";

.card-container {
  position: relative;
}

.book-card {
  max-width: 250px;
  margin: 0 auto;
}

.cover-image {
  width: 100%;
  aspect-ratio: 0.625;
  background-color: var(--v-lightestAccent-base);
}

.book-title {
  font-size: 0.875rem;
  font-weight: 500;
}

.book-price {
  font-size: 0.75rem;
}

.format-chip {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: $z-base;
}

.owned-icon {
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: $z-base;
  background-color: var(--v-secondary-base);
}

.buy-now-actions ::v-deep .v-btn {
  font-size: 0.625rem !important;
  letter-spacing: 0.02em;
  padding: 0 6px !important;
}

.actions-container {
  height: 88px;
}

@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .actions-container {
    height: 72px;
  }
}

@media #{map-get($display-breakpoints, 'xs-only')} {
  .book-title {
    font-size: 0.75rem;
  }

  .book-price {
    font-size: 0.625rem;
  }

  .actions-container {
    height: 56px;
  }

  .buy-now-actions ::v-deep .v-btn {
    font-size: 0.5625rem !important;
    padding: 0 4px !important;
  }
}
</style>